import React, { useEffect, useRef } from 'react';
import useVisualViewport from '../../hooks/useVisualViewport';
import './Modal.css';
import Icon from './Icon';

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * `dirty` blocks closing by overlay tap and Esc. Edits made in fields inside the modal are also
 * detected automatically. The × button and explicit actions always close.
 * `footer` renders actions pinned below the scrolling body. `size`: sm | md | lg.
 */
const Modal = ({ show, onClose, title, children, footer = null, dirty = false, size = 'md', className = '' }) => {
    const overlayRef = useRef(null);
    const dialogRef = useRef(null);
    const editedRef = useRef(false);
    const pressStartedOnOverlayRef = useRef(false);
    const canDismissRef = useRef(() => true);
    const onCloseRef = useRef(onClose);

    canDismissRef.current = () => !dirty && !editedRef.current;
    onCloseRef.current = onClose;

    useVisualViewport(overlayRef, show);

    useEffect(() => {
        if (!show) return undefined;

        editedRef.current = false;
        const previouslyFocused = document.activeElement;

        // Children focus their own first control in effects that run first; do not steal it.
        if (dialogRef.current && !dialogRef.current.contains(document.activeElement)) {
            dialogRef.current.focus({ preventScroll: true });
        }

        const handleKeyDown = (event) => {
            if (event.key === 'Escape' && canDismissRef.current()) {
                event.stopPropagation();
                onCloseRef.current();
                return;
            }

            if (event.key !== 'Tab' || !dialogRef.current) return;
            const focusable = dialogRef.current.querySelectorAll(FOCUSABLE);
            if (!focusable.length) return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            if (previouslyFocused?.isConnected) previouslyFocused.focus?.({ preventScroll: true });
        };
    }, [show]);

    if (!show) return null;

    const markEdited = (event) => {
        if (event.target.matches('input, textarea, select')) {
            editedRef.current = true;
        }
    };

    const handleOverlayClick = (event) => {
        // Ignore a text-selection drag that started inside the dialog and ended on the overlay.
        const isOverlayTap = event.target === event.currentTarget && pressStartedOnOverlayRef.current;
        if (isOverlayTap && canDismissRef.current()) {
            onClose();
        }
    };

    return (
        <div
            ref={overlayRef}
            className={`modal-overlay${className ? ` ${className}-overlay` : ''}`}
            onPointerDown={(event) => {
                pressStartedOnOverlayRef.current = event.target === event.currentTarget;
            }}
            onClick={handleOverlayClick}
        >
            <div
                ref={dialogRef}
                className={['modal-content', `modal-${size}`, className].filter(Boolean).join(' ')}
                role="dialog"
                aria-modal="true"
                aria-label={typeof title === 'string' ? title : undefined}
                tabIndex={-1}
                onClick={(event) => event.stopPropagation()}
                onInput={markEdited}
                onChange={markEdited}
            >
                <div className="modal-header">
                    <h3>{title}</h3>
                    <button type="button" className="close-btn" aria-label="Закрыть" onClick={onClose}>
                        <Icon name="times" />
                    </button>
                </div>
                <div className="modal-body">
                    {children}
                </div>
                {footer && <div className="modal-footer">{footer}</div>}
            </div>
        </div>
    );
};

export default Modal;
